import axios from 'axios';
import { ICrawlerRepository } from '../../application/ports/ICrawlerRepository';
import { CrawlerEntity, CrawlerImportPayload } from '../../domain/entities/Crawler';
import { CrawlerChannelEntity } from '../../domain/entities/CrawlerChannel';
import { advertizerApiHost } from '../../constants/envs';
import { AuthTokenProvider } from '../auth/AuthTokenProvider';

const BASE_PATH = '/api/admin/crawlers';

interface CrawlerDTO {
  uuid: string;
  phone: string;
  status: CrawlerEntity['status'];
  connectedAt?: string | null;
  telegramKnownSince?: string | null;
  cooldownUntil?: string | null;
  pausedReason?: string;
  ageDays?: number;
  releaseNeedsWarning?: boolean;
  createdAt: string;
  source?: CrawlerEntity['source'];
  importError?: string;
  importAttempts?: number;
  listeningChannels?: number;
  pendingChannels?: number;
}

const toEntity = (dto: CrawlerDTO): CrawlerEntity => ({
  uuid: dto.uuid,
  phone: dto.phone,
  status: dto.status,
  connectedAt: dto.connectedAt ?? undefined,
  telegramKnownSince: dto.telegramKnownSince ?? undefined,
  cooldownUntil: dto.cooldownUntil ?? undefined,
  pausedReason: dto.pausedReason ?? '',
  ageDays: dto.ageDays ?? 0,
  releaseNeedsWarning: dto.releaseNeedsWarning ?? false,
  createdAt: dto.createdAt,
  source: dto.source ?? 'manual',
  importError: dto.importError ?? '',
  importAttempts: dto.importAttempts ?? 0,
  listeningChannels: dto.listeningChannels ?? 0,
  pendingChannels: dto.pendingChannels ?? 0,
});

interface CrawlerChannelDTO {
  id: number;
  link: string;
  title?: string;
  telegramChannelId?: number;
  status: CrawlerChannelEntity['status'];
  source: CrawlerChannelEntity['source'];
  attempts?: number;
  lastError?: string;
  joinedAt?: string | null;
  assignedAt?: string | null;
}

const toChannelEntity = (dto: CrawlerChannelDTO): CrawlerChannelEntity => ({
  id: dto.id,
  link: dto.link,
  title: dto.title ?? '',
  telegramChannelId: dto.telegramChannelId ?? 0,
  status: dto.status,
  source: dto.source,
  attempts: dto.attempts ?? 0,
  lastError: dto.lastError ?? '',
  joinedAt: dto.joinedAt ?? undefined,
  assignedAt: dto.assignedAt ?? undefined,
});

export class CrawlersAxiosRepository implements ICrawlerRepository {
  constructor(private auth: AuthTokenProvider) {}

  private async authHeaders() {
    const token = await this.auth.getToken();
    if (!token) throw new Error('Unauthenticated');
    return { Authorization: `Bearer ${token}` };
  }

  async list(): Promise<CrawlerEntity[]> {
    const headers = await this.authHeaders();
    const res = await axios.get(`${advertizerApiHost}${BASE_PATH}`, { headers });
    return (res.data.crawlers ?? []).map(toEntity);
  }

  async add(phone: string): Promise<string> {
    const headers = await this.authHeaders();
    const res = await axios.post(`${advertizerApiHost}${BASE_PATH}`, { phone }, { headers });
    return res.data.uuid;
  }

  async importFromSession(payload: CrawlerImportPayload): Promise<string> {
    const headers = await this.authHeaders();
    const form = new FormData();
    form.append('profile', payload.profileFile);
    form.append('session', payload.sessionFile);
    const res = await axios.post(`${advertizerApiHost}${BASE_PATH}/import`, form, {
      headers: { ...headers, 'Content-Type': 'multipart/form-data' },
    });
    return res.data.uuid;
  }

  async requestCode(uuid: string): Promise<void> {
    const headers = await this.authHeaders();
    await axios.post(`${advertizerApiHost}${BASE_PATH}/${uuid}/auth/start`, null, { headers });
  }

  async confirmCode(uuid: string, code: string, password: string): Promise<void> {
    const headers = await this.authHeaders();
    await axios.post(`${advertizerApiHost}${BASE_PATH}/${uuid}/auth/confirm`, { code, password }, { headers });
  }

  async release(uuid: string): Promise<void> {
    const headers = await this.authHeaders();
    await axios.post(`${advertizerApiHost}${BASE_PATH}/${uuid}/release`, null, { headers });
  }

  async listChannels(uuid: string): Promise<CrawlerChannelEntity[]> {
    const headers = await this.authHeaders();
    const res = await axios.get(`${advertizerApiHost}${BASE_PATH}/${uuid}/channels`, { headers });
    return (res.data.channels ?? []).map(toChannelEntity);
  }

  async assignChannels(uuid: string, channelUuids: string[]): Promise<void> {
    const headers = await this.authHeaders();
    await axios.post(`${advertizerApiHost}${BASE_PATH}/${uuid}/channels`, { channelUuids }, { headers });
  }

  async unassignChannel(uuid: string, id: number): Promise<void> {
    const headers = await this.authHeaders();
    await axios.delete(`${advertizerApiHost}${BASE_PATH}/${uuid}/channels/${id}`, { headers });
  }
}
